import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  RotateCcw,
  ShoppingCart,
  Boxes,
  IndianRupee,
  Layers,
  Tag,
  Barcode,
  Package,
  ShieldCheck,
  AlertCircle,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, StandardImage } from "@/components/shared";
import { getApiErrorMessage } from "@/services/httpClient";
import { channelOrdersService, type ChannelCatalogueItem } from "@/services/channelOrdersService";
import { categoriesService } from "@/services/categoriesService";
import { useChannelDesk } from "@/lib/channel-desk";

export function getPlaceOrderUrl(base: string, productId: number | string): string {
  if (base === "/store/supply") {
    return `/store/supply/place-order/${productId}`;
  }
  return `${base}/catalogue/place-order/${productId}`;
}

export function getOrdersUrl(base: string): string {
  return base === "/store/supply" ? "/store/supply/orders" : `${base}/orders`;
}

export default function DistributorCatalogue() {
  const location = useLocation();
  const desk = useChannelDesk();
  const ordersUrl = getOrdersUrl(desk.base);

  const userRoleType = useMemo<"store" | "wholesaler" | "distributor">(() => {
    if (location.pathname.startsWith("/store") || desk.base === "/store/supply" || desk.role === "Retailer") {
      return "store";
    }
    if (location.pathname.startsWith("/wholesaler") || desk.base === "/wholesaler" || desk.role === "Wholesaler") {
      return "wholesaler";
    }
    return "distributor";
  }, [location.pathname, desk.base, desk.role]);

  const getRolePurchaseInfo = (item: ChannelCatalogueItem) => {
    if (userRoleType === "store") {
      const price = item.retailPrice && item.retailPrice > 0 ? item.retailPrice : item.price;
      return {
        label: "Store Buy Price (RP)",
        badge: "RP",
        price,
        customerPrice: item.customerPrice > 0 ? item.customerPrice : item.price,
      };
    }
    if (userRoleType === "wholesaler") {
      const price = item.wholesalePrice && item.wholesalePrice > 0 ? item.wholesalePrice : item.price;
      return {
        label: "Wholesaler Buy Price (WP)",
        badge: "WP",
        price,
        customerPrice: item.customerPrice > 0 ? item.customerPrice : item.price,
      };
    }
    const price = item.distributorPrice && item.distributorPrice > 0 ? item.distributorPrice : item.price;
    return {
      label: "Distributor Buy Price (DP)",
      badge: "DP",
      price,
      customerPrice: item.customerPrice > 0 ? item.customerPrice : item.price,
    };
  };

  // Search & Filter State
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockAvailabilityFilter, setStockAvailabilityFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  // Query Catalogue with Backend Filtering
  const catalogueQuery = useQuery({
    queryKey: ["channel", "catalogue", userRoleType, desk.base, appliedSearch, categoryFilter],
    queryFn: () =>
      channelOrdersService.catalogue({
        search: appliedSearch || undefined,
        category: categoryFilter !== "all" ? categoryFilter : undefined,
      }),
  });

  // Query Categories for filter dropdown
  const categoriesQuery = useQuery({
    queryKey: ["categories", "root"],
    queryFn: () => categoriesService.getRoot(),
    staleTime: 5 * 60 * 1000,
  });

  const rawProducts = catalogueQuery.data ?? [];

  // Filter products by stock availability on frontend if requested
  const products = useMemo(() => {
    if (stockAvailabilityFilter === "in_stock") {
      return rawProducts.filter((p) => p.availableQuantity > 0);
    }
    if (stockAvailabilityFilter === "out_of_stock") {
      return rawProducts.filter((p) => p.availableQuantity <= 0);
    }
    return rawProducts;
  }, [rawProducts, stockAvailabilityFilter]);

  // Extract distinct categories
  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    (categoriesQuery.data ?? []).forEach((c) => {
      if (c.name) set.add(c.name);
    });
    rawProducts.forEach((p) => {
      if (p.categoryName) set.add(p.categoryName);
    });
    return Array.from(set).sort();
  }, [categoriesQuery.data, rawProducts]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [appliedSearch, categoryFilter, stockAvailabilityFilter]);

  const totalPages = Math.ceil(products.length / pageSize) || 1;
  const pagedProducts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return products.slice(start, start + pageSize);
  }, [products, page, pageSize]);

  // Search Form Submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
  };

  // Reset Filters
  const handleResetFilters = () => {
    setSearchInput("");
    setAppliedSearch("");
    setCategoryFilter("all");
    setStockAvailabilityFilter("all");
    setPage(1);
  };

  const isFiltered = Boolean(
    appliedSearch || categoryFilter !== "all" || stockAvailabilityFilter !== "all"
  );

  const formatInr = (amt: number) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

  return (
    <div className="space-y-6 pb-12">
      {/* Page Header */}
      <PageHeader
        title={desk.catalogueTitle}
        description={desk.summary}
        actions={
          <Button asChild variant="outline" className="gap-2 shadow-sm">
            <Link to={ordersUrl}>
              <Clock className="h-4 w-4" />
              View Your Channel Orders
            </Link>
          </Button>
        }
      />

      {/* Top Search & Filter Bar */}
      <Card className="border border-border/80 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <form onSubmit={handleSearchSubmit} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
              {/* Search Field */}
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search Product by Name, SKU, Code or Category..."
                  className="pl-10 h-10 w-full bg-background"
                />
              </div>

              {/* Action Buttons for Search */}
              <div className="flex items-center gap-2 shrink-0">
                <Button type="submit" className="h-10 px-4 gap-2">
                  <Search className="h-4 w-4" />
                  Search
                </Button>
                {isFiltered && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResetFilters}
                    className="h-10 px-3 gap-1.5 text-muted-foreground hover:text-foreground"
                    title="Reset all filters"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reset
                  </Button>
                )}
              </div>

              {/* Filter Dropdowns */}
              <div className="flex flex-col sm:flex-row gap-2.5 shrink-0">
                {/* Category Dropdown */}
                <div className="w-full sm:w-[170px]">
                  <Select
                    value={categoryFilter}
                    onValueChange={(val) => setCategoryFilter(val)}
                  >
                    <SelectTrigger className="h-10 bg-background text-sm">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {availableCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stock Availability Dropdown */}
                <div className="w-full sm:w-[150px]">
                  <Select
                    value={stockAvailabilityFilter}
                    onValueChange={(val) => setStockAvailabilityFilter(val)}
                  >
                    <SelectTrigger className="h-10 bg-background text-sm">
                      <SelectValue placeholder="Stock Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      <SelectItem value="in_stock">In Stock Only</SelectItem>
                      <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Active Filters Pill Bar */}
            {isFiltered && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Active Filters:</span>
                {appliedSearch && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Search: &ldquo;{appliedSearch}&rdquo;
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        setAppliedSearch("");
                      }}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {categoryFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Category: {categoryFilter}
                    <button
                      type="button"
                      onClick={() => setCategoryFilter("all")}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {stockAvailabilityFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Availability: {stockAvailabilityFilter.replace("_", " ")}
                    <button
                      type="button"
                      onClick={() => setStockAvailabilityFilter("all")}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetFilters}
                  className="h-6 px-2 text-xs text-primary hover:text-primary"
                >
                  Clear all
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Catalogue Product Cards Grid */}
      {catalogueQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="overflow-hidden border border-border/80">
              <Skeleton className="h-48 w-full" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-9 w-full rounded-lg" />
              </div>
            </Card>
          ))}
        </div>
      ) : catalogueQuery.isError ? (
        <Card className="border-destructive/30 p-8 text-center space-y-3">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="font-semibold text-destructive">Failed to load catalogue</p>
          <p className="text-sm text-muted-foreground">{getApiErrorMessage(catalogueQuery.error)}</p>
          <Button variant="outline" onClick={() => catalogueQuery.refetch()}>
            Try Again
          </Button>
        </Card>
      ) : products.length === 0 ? (
        <Card className="p-12 text-center space-y-4 border-dashed">
          <Package className="h-12 w-12 text-muted-foreground/60 mx-auto" />
          <div className="space-y-1">
            <h3 className="font-bold text-lg text-foreground">No Products Found</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {isFiltered
                ? "No products matched your search or filter criteria. Try clearing your filters."
                : desk.catalogueEmpty}
            </p>
          </div>
          {isFiltered && (
            <Button variant="outline" onClick={handleResetFilters} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset Filters
            </Button>
          )}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {pagedProducts.map((item) => {
              const hasStock = item.availableQuantity > 0;
              const placeOrderUrl = getPlaceOrderUrl(desk.base, item.productId);

              return (
                <Card
                  key={item.productId}
                  className="overflow-hidden border border-border/80 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group"
                >
                  <div>
                    {/* Product Image Container */}
                    <div className="relative h-48 w-full bg-white p-3 flex items-center justify-center border-b overflow-hidden">
                      <StandardImage
                        src={item.imageUrl}
                        alt={item.name}
                        standard="product"
                        className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />

                      {/* Stock Status Badge Overlay */}
                      <div className="absolute top-2.5 right-2.5">
                        {hasStock ? (
                          <Badge
                            variant="outline"
                            className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 font-bold text-[11px] shadow-sm backdrop-blur-sm"
                          >
                            {item.availableQuantity} in stock
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="bg-destructive/10 text-destructive border-destructive/20 font-bold text-[11px] shadow-sm backdrop-blur-sm"
                          >
                            Out of Stock
                          </Badge>
                        )}
                      </div>

                      {/* Category Pill Overlay */}
                      {item.categoryName && (
                        <div className="absolute top-2.5 left-2.5">
                          <Badge variant="secondary" className="text-[10px] py-0 px-1.5 font-medium shadow-sm">
                            {item.categoryName}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Product Info Section */}
                    <CardContent className="p-4 space-y-2.5">
                      <div>
                        <h3
                          className="font-bold text-base text-foreground leading-snug line-clamp-2 hover:text-primary transition-colors"
                          title={item.name}
                        >
                          {item.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                          {item.sku ? (
                            <span className="font-mono text-[11px] font-medium text-foreground/80">
                              SKU: {item.sku}
                            </span>
                          ) : null}
                          {item.unit ? (
                            <span className="text-[11px]">
                              · {item.unitValue ? `${item.unitValue} ` : ""}{item.unit}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {/* Price & BV Section */}
                      {(() => {
                        const priceInfo = getRolePurchaseInfo(item);
                        const margin = priceInfo.customerPrice > priceInfo.price ? priceInfo.customerPrice - priceInfo.price : 0;
                        return (
                          <div className="pt-2 border-t flex items-baseline justify-between gap-2">
                            <div className="space-y-0.5">
                              <span className="text-[10px] text-muted-foreground uppercase font-semibold">
                                {priceInfo.label}
                              </span>
                              <div className="flex items-baseline gap-1.5">
                                <span className="font-extrabold text-lg text-primary">
                                  {formatInr(priceInfo.price)}
                                </span>
                                {item.mrp > priceInfo.price && (
                                  <span className="text-xs text-muted-foreground line-through">
                                    MRP {formatInr(item.mrp)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <span>CP: {formatInr(priceInfo.customerPrice)}</span>
                                {margin > 0 && (
                                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                    · +{formatInr(margin)} margin
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-right">
                              <Badge
                                variant="secondary"
                                className="font-bold text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
                              >
                                {item.businessVolume} BV
                              </Badge>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Commission Benefit Preview */}
                      {item.onwardCommissionPercent > 0 && (
                        <div className="p-2 rounded-lg bg-primary/5 border border-primary/10 text-[11px] text-primary flex items-center gap-1.5">
                          <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                          <span>
                            Earn <strong>{item.onwardCommissionPercent}%</strong> when {desk.earnWhen}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </div>

                  {/* Card Action Footer */}
                  <CardFooter className="p-4 pt-0">
                    {hasStock ? (
                      <Button asChild className="w-full gap-2 font-bold shadow-sm" size="sm">
                        <Link to={placeOrderUrl}>
                          <ShoppingCart className="h-4 w-4" />
                          Place Order
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        disabled
                        variant="outline"
                        className="w-full font-medium text-muted-foreground cursor-not-allowed bg-muted/30"
                        size="sm"
                      >
                        Out of Stock
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {products.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t mt-4">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-semibold text-foreground">{(page - 1) * pageSize + 1}</span> to{" "}
                <span className="font-semibold text-foreground">{Math.min(page * pageSize, products.length)}</span> of{" "}
                <span className="font-semibold text-foreground">{products.length}</span> products
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-sm">
                  <span className="text-muted-foreground text-xs">Per page:</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(val) => {
                      setPageSize(Number(val));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-18 text-xs bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="24">24</SelectItem>
                      <SelectItem value="48">48</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="sr-only">Previous page</span>
                  </Button>
                  <div className="text-xs font-semibold px-2">
                    Page {page} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                    <span className="sr-only">Next page</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
